import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Clock, AlertTriangle, Zap, Moon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface DraftTimerProps {
  timeRemaining: number;
  totalTime: number;
  isOnTheClock: boolean;
  onTheClockTeamName: string;
  isTimeCrunched: boolean;
  isPaused: boolean;
  isSlowDraft?: boolean;
  overnightPauseActive?: boolean;
  pauseEndHour?: number;
  isHalfTimeActive?: boolean;
}

/** Pizza-tracker stages for slow draft countdown */
const SLOW_DRAFT_STAGES = [
  { label: 'Pick Started', icon: '🕐', threshold: 1.0 },
  { label: 'Halfway', icon: '⏳', threshold: 0.5 },
  { label: 'Final Hour', icon: '⚠️', threshold: -1 }, // special: 1 hour remaining
  { label: 'Expiring', icon: '🔴', threshold: 0.0 },
] as const;

function getActiveStageIndex(timeRemaining: number, totalTime: number): number {
  if (totalTime <= 0) return 0;
  const fraction = timeRemaining / totalTime;
  // Stage 0: always active once pick starts
  // Stage 1: active when <= 50% time remaining (Halfway)
  // Stage 2: active when <= 60 min remaining (Final Hour)
  // Stage 3: active when <= 15 min remaining (Expiring — pulses red)
  if (timeRemaining <= 900) return 3;   // 15 min
  if (timeRemaining <= 3600) return 2;  // 60 min
  if (fraction <= 0.5) return 1;
  return 0;
}

const SlowDraftTracker = ({ timeRemaining, totalTime }: { timeRemaining: number; totalTime: number }) => {
  const activeIdx = getActiveStageIndex(timeRemaining, totalTime);
  const isExpiring = timeRemaining <= 900; // under 15 min

  return (
    <div className="space-y-2">
      {/* Step tracker */}
      <div className="flex items-center gap-0">
        {SLOW_DRAFT_STAGES.map((stage, i) => {
          const isActive = i <= activeIdx;
          const isCurrent = i === activeIdx;
          return (
            <div key={stage.label} className="flex items-center flex-1 last:flex-initial">
              {/* Step node */}
              <div className={cn(
                "flex flex-col items-center gap-1 min-w-[60px]",
                isCurrent && isExpiring && "animate-pulse"
              )}>
                <div className={cn(
                  "w-9 h-9 rounded-full flex items-center justify-center text-sm border-2 transition-all",
                  isActive
                    ? isCurrent && isExpiring
                      ? "border-red-500 bg-red-500/20 shadow-lg shadow-red-500/30"
                      : isCurrent
                      ? "border-primary bg-primary/20 shadow-lg shadow-primary/20"
                      : "border-primary/50 bg-primary/10"
                    : "border-muted bg-muted/30"
                )}>
                  <span className={cn(!isActive && "grayscale opacity-40")}>{stage.icon}</span>
                </div>
                <span className={cn(
                  "text-[10px] font-medium leading-tight text-center",
                  isActive
                    ? isCurrent && isExpiring ? "text-red-400" : "text-foreground"
                    : "text-muted-foreground/50"
                )}>
                  {stage.label}
                </span>
              </div>
              {/* Connector line */}
              {i < SLOW_DRAFT_STAGES.length - 1 && (
                <div className={cn(
                  "h-0.5 flex-1 mx-1 rounded-full transition-all",
                  i < activeIdx ? "bg-primary/50" : "bg-muted/40"
                )} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const DraftTimer = ({
  timeRemaining,
  totalTime,
  isOnTheClock,
  onTheClockTeamName,
  isTimeCrunched,
  isPaused,
  isSlowDraft = false,
  overnightPauseActive = false,
  pauseEndHour = 8,
  isHalfTimeActive = false,
}: DraftTimerProps) => {
  const percentage = totalTime > 0 ? (timeRemaining / totalTime) * 100 : 0;

  // For slow drafts, thresholds match pizza tracker stages
  const isLowTime = isSlowDraft ? timeRemaining <= 3600 : timeRemaining <= 15; // 1 hour or 15 sec
  const isCritical = isSlowDraft ? timeRemaining <= 900 : timeRemaining <= 5;  // 15 min or 5 sec

  const formatTime = (seconds: number) => {
    if (isSlowDraft) {
      const hours = Math.floor(seconds / 3600);
      const mins = Math.floor((seconds % 3600) / 60);
      if (hours > 0) {
        return `${hours}h ${mins.toString().padStart(2, '0')}m remaining`;
      }
      if (mins > 0) {
        const secs = seconds % 60;
        return `${mins}m ${secs.toString().padStart(2, '0')}s remaining`;
      }
      return `${seconds}s remaining`;
    }
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatPauseEnd = () => {
    const hour = pauseEndHour;
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:00 ${ampm}`;
  };

  return (
    <Card className={cn(
      "glass-card transition-all",
      isOnTheClock && "border-primary ring-2 ring-primary/30",
      isTimeCrunched && "border-warning ring-2 ring-warning/50",
      isCritical && !isPaused && "border-damage ring-2 ring-damage/50 animate-pulse"
    )}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className={cn(
              "w-10 h-10 rounded-full flex items-center justify-center",
              isPaused || overnightPauseActive ? "bg-muted" :
              isCritical ? "bg-damage/20" : isLowTime ? "bg-warning/20" : "bg-primary/20"
            )}>
              {overnightPauseActive ? (
                <Moon className="w-5 h-5 text-muted-foreground" />
              ) : (
                <Clock className={cn(
                  "w-5 h-5",
                  isPaused ? "text-muted-foreground" :
                  isCritical ? "text-damage" : isLowTime ? "text-warning" : "text-primary"
                )} />
              )}
            </div>
            <div className="min-w-0">
              <p className="text-sm text-muted-foreground">On the Clock</p>
              <p className="font-bold truncate max-w-[140px] sm:max-w-[200px] lg:max-w-none">{onTheClockTeamName}</p>
            </div>
          </div>
          <div className="text-right">
            {overnightPauseActive ? (
              <div>
                <div className="text-lg font-medium text-muted-foreground">
                  Paused overnight
                </div>
                <p className="text-sm text-muted-foreground">
                  Resumes at {formatPauseEnd()}
                </p>
              </div>
            ) : isPaused ? (
              <div>
                <div className="text-2xl font-mono font-bold text-muted-foreground">
                  PAUSED
                </div>
              </div>
            ) : (
              <div>
                <div className={cn(
                  "font-mono font-bold",
                  isSlowDraft ? "text-lg" : "text-3xl",
                  isCritical ? "text-damage" : isLowTime ? "text-warning" : "text-foreground",
                  isSlowDraft && isCritical && "animate-pulse"
                )}>
                  {formatTime(timeRemaining)}
                </div>
              </div>
            )}
            {isHalfTimeActive && (
              <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30 gap-1">
                <Clock className="w-3 h-3" />
                Half Time!
              </Badge>
            )}
            {isTimeCrunched && (
              <Badge className="bg-warning/20 text-warning border-warning/30 gap-1">
                <Zap className="w-3 h-3" />
                Time Crunch!
              </Badge>
            )}
          </div>
        </div>

        {/* Slow draft: pizza tracker. Live draft: progress bar */}
        {!overnightPauseActive && !isPaused && (
          isSlowDraft ? (
            <SlowDraftTracker timeRemaining={timeRemaining} totalTime={totalTime} />
          ) : (
            <Progress
              value={percentage}
              className={cn(
                "h-2",
                isCritical && "[&>div]:bg-damage",
                isLowTime && !isCritical && "[&>div]:bg-warning"
              )}
            />
          )
        )}

        {isOnTheClock && !isPaused && !overnightPauseActive && (
          <div className="mt-3 flex items-center justify-center gap-2 p-2 rounded-lg bg-primary/10 text-primary">
            <AlertTriangle className="w-4 h-4" />
            <span className="text-sm font-medium">
              {isSlowDraft ? "It's your turn. Make your pick!" : "It's your turn to pick!"}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DraftTimer;
